import clsx from 'clsx'
import PropTypes from 'prop-types'
import { Fragment, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import TargetGroupFilter, { targetGroupOptions, matchesTargetGroup } from './TargetGroupFilter'
import styles from './style.module.scss'

/*
 * Übersicht über alle Nachrichten entlang des Lebenszyklus einer Junior Company
 * — ein Balkenplan (Gantt), bei dem die Zeitachse aus Phasen besteht und nicht
 * aus Kalenderdaten. Je Zeile eine Vorlage: links Kanal und Titel, im Balken der
 * Auslöser, waagrecht die Phase(n), in denen sie versendet wird.
 *
 * Die Einordnung kommt vom Server (services/communication/lifecycle.js), damit
 * Katalog und Ablauf nicht auseinanderlaufen. Hier wird nur gezeichnet und
 * gefiltert.
 *
 * Ein Klick auf Zeile oder Balken öffnet dieselbe Detailansicht wie die Listen.
 */

const LABEL_COLUMN = '320px'
const PHASE_COLUMN = 'minmax(150px, 1fr)'

const EmailIcon = () => (
	<svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
		<rect x="1" y="3" width="14" height="10" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
		<path d="M1.5 4l6.5 5 6.5-5" fill="none" stroke="currentColor" strokeWidth="1.5" />
	</svg>
)

const InternalIcon = () => (
	<svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
		<path
			d="M2 3.5h12v8H6.5L3 14v-2.5H2z"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinejoin="round"
		/>
	</svg>
)

const ChannelChip = ({ channel, label }) => (
	<span className={clsx(styles.chanChip, channel === 'email' ? styles.chanChipEmail : styles.chanChipInternal)}>
		{channel === 'email' ? <EmailIcon /> : <InternalIcon />}
		{label}
	</span>
)

ChannelChip.propTypes = {
	channel: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired
}

/**
 * Filtert die Zeilen nach Kanal, Zielgruppe und Suchbegriff.
 * Ausgelagert, weil ein Fehler hier Vorlagen still verschwinden lässt.
 */
export const filterItems = (items, { channel, targetGroup, search }) => {
	const q = (search || '').trim().toLowerCase()
	return items.filter(item => {
		if (channel && item.channel !== channel) return false
		if (!matchesTargetGroup(item, targetGroup)) return false
		if (!q) return true
		return [item.title, item.trigger, item.key].filter(Boolean).some(field => field.toLowerCase().includes(q))
	})
}

/**
 * Baut die Zeilenfolge des Rasters: je Spur eine Überschrift, darunter die
 * Vorlagen dieser Spur. Spuren ohne Treffer entfallen, damit ein Filter keine
 * leeren Bänder stehen lässt.
 */
export const buildRows = (tracks, items) => {
	const out = []
	tracks.forEach(track => {
		const own = items.filter(item => item.track === track.key)
		if (!own.length) return
		out.push({ type: 'track', id: `track:${track.key}`, track, count: own.length })
		own.forEach(item => out.push({ type: 'item', id: `${item.channel}:${item.key}`, item }))
	})
	return out
}

/**
 * Erste und letzte Phase eines Eintrags als Spaltennummern. Unbekannte Phasen
 * (Katalog neuer als die Ansicht) landen in der ersten Spalte, statt den Balken
 * verschwinden zu lassen; ein verdrehtes Paar wird nicht negativ.
 */
const spanOf = (item, phasePosition) => {
	const from = phasePosition[item.phaseFrom] ?? 0
	const to = Math.max(phasePosition[item.phaseTo] ?? from, from)
	return { from, to }
}

/**
 * Rasterspalten der Zeile: vom Balkenanfang bis zum rechten Rand der Achse.
 * Spalte 1 ist die Titelspalte, deshalb der Versatz um 1.
 */
export const entryColumns = (item, phasePosition) => `${spanOf(item, phasePosition).from + 2} / -1`

/**
 * Breite des Balkens innerhalb dieser Zeile, in Prozent. Alle Phasenspalten sind
 * gleich breit, deshalb ist das Verhältnis der Spaltenzahlen exakt.
 */
export const barWidthPercent = (item, phasePosition, phaseCount) => {
	const { from, to } = spanOf(item, phasePosition)
	const rest = Math.max(phaseCount - from, 1)
	return Math.min(((to - from + 1) / rest) * 100, 100)
}

export default function OverviewGantt({ data, onOpen }) {
	const { t } = useTranslation()

	// Über useMemo, damit die abgeleiteten Listen unten nicht bei jedem Render neu
	// berechnet werden (leeres Array wäre sonst jedes Mal eine neue Referenz).
	const phases = useMemo(() => data?.phases || [], [data])
	const tracks = useMemo(() => data?.tracks || [], [data])
	const items = useMemo(() => data?.items || [], [data])

	const [channel, setChannel] = useState('')
	const [targetGroup, setTargetGroup] = useState('')
	const [search, setSearch] = useState('')

	const groupOptions = useMemo(() => targetGroupOptions(items), [items])

	const phasePosition = useMemo(() => {
		const map = {}
		phases.forEach((phase, index) => {
			map[phase.key] = index
		})
		return map
	}, [phases])

	const filtered = useMemo(
		() => filterItems(items, { channel, targetGroup, search }),
		[items, channel, targetGroup, search]
	)

	const rows = useMemo(() => buildRows(tracks, filtered), [tracks, filtered])

	const gridTemplateColumns = `${LABEL_COLUMN} repeat(${phases.length}, ${PHASE_COLUMN})`
	const channelLabel = value => (value === 'email' ? t('Email') : t('Internal'))

	return (
		<div className={styles.listWrap}>
			<div className={styles.listToolbar}>
				<p className={styles.counter}>
					{filtered.length} {t('Templates')} · {phases.length} {t('Phases')}
				</p>
				<input
					type="text"
					className={styles.search}
					value={search}
					onChange={e => setSearch(e.target.value)}
					placeholder={t('Search')}
				/>
			</div>

			<p className={styles.overviewNote}>
				{t(
					'The axis shows the phases of a Junior Company, not calendar dates. Click a bar for the template details.'
				)}
			</p>

			<div className={styles.groupFilter}>
				<span className={styles.groupFilterLabel}>{t('Channel')}:</span>
				<button
					type="button"
					className={clsx(styles.groupChip, !channel && styles.groupChipActive)}
					onClick={() => setChannel('')}
				>
					{t('All')}
				</button>
				<button
					type="button"
					className={clsx(styles.groupChip, channel === 'email' && styles.groupChipActive)}
					onClick={() => setChannel(channel === 'email' ? '' : 'email')}
				>
					{t('Email templates')}
				</button>
				<button
					type="button"
					className={clsx(styles.groupChip, channel === 'internal' && styles.groupChipActive)}
					onClick={() => setChannel(channel === 'internal' ? '' : 'internal')}
				>
					{t('Internal messages')}
				</button>
			</div>

			<TargetGroupFilter options={groupOptions} value={targetGroup} onChange={setTargetGroup} />

			<div className={styles.legend}>
				<span className={styles.legendItem}>
					<span className={clsx(styles.legendSwatch, styles.legendSwatchEmail)} />
					{t('Email templates')}
				</span>
				<span className={styles.legendItem}>
					<span className={clsx(styles.legendSwatch, styles.legendSwatchInternal)} />
					{t('Internal messages')}
				</span>
				<span className={styles.legendItem}>
					<span className={clsx(styles.legendSwatch, styles.legendSwatchInactive)} />
					{t('Inactive')}
				</span>
				<span className={styles.legendItem}>
					<span className={styles.legendAuto}>{t('Automatic')}</span>
					{t('sent by the nightly reminder job')}
				</span>
			</div>

			{rows.length === 0 ? (
				<p className={styles.overviewEmpty}>{t('No templates match the current filter.')}</p>
			) : (
				<div className={styles.chartScroll}>
					<div className={styles.chart} style={{ gridTemplateColumns }}>
						{/* Spaltenraster hinter den Zeilen — nur Optik. */}
						<div
							className={styles.stripes}
							aria-hidden="true"
							style={{
								gridRow: `1 / ${rows.length + 2}`,
								gridTemplateColumns: `repeat(${phases.length}, 1fr)`
							}}
						>
							{phases.map(phase => (
								<div key={phase.key} className={styles.stripe} />
							))}
						</div>

						<div className={styles.headCorner} style={{ gridRow: 1, gridColumn: 1 }}>
							{t('Template')}
						</div>
						{phases.map((phase, index) => (
							<div
								key={phase.key}
								className={styles.headPhase}
								style={{ gridRow: 1, gridColumn: index + 2 }}
								title={phase.description}
							>
								<span className={styles.headPhaseNo}>{index + 1}</span>
								<span className={styles.headPhaseTitle}>{phase.title}</span>
							</div>
						))}

						{rows.map((row, index) => {
							const gridRow = index + 2

							if (row.type === 'track') {
								return (
									<div
										key={row.id}
										className={styles.trackHead}
										style={{ gridRow, gridColumn: `1 / ${phases.length + 2}` }}
									>
										{row.track.title}
										<span className={styles.trackCount}>{row.count}</span>
									</div>
								)
							}

							const { item } = row
							const open = () => onOpen(item.channel, item.key)

							return (
								<Fragment key={row.id}>
									<button
										type="button"
										className={clsx(styles.rowLabel, !item.isActive && styles.rowLabelInactive)}
										style={{ gridRow, gridColumn: 1 }}
										onClick={open}
										title={`${item.title} — ${item.trigger}`}
									>
										<ChannelChip channel={item.channel} label={channelLabel(item.channel)} />
										<span className={styles.rowTitle}>{item.title}</span>
										{!item.isActive && <span className={styles.rowFlag}>{t('Inactive')}</span>}
									</button>

									{/*
									 * Wie auf der Zeitachse der Nachrichten: die Zeile reicht vom
									 * Balkenanfang bis zum rechten Rand, der Balken ist ihr erstes
									 * Stück. Die Beschriftung beginnt an derselben Kante und darf
									 * über das Balkenende hinauslaufen — sonst bliebe von "wenn
									 * eine Junior Company angemeldet wird" bei einphasigen
									 * Balken nur "wenn eine Juni…".
									 */}
									<span
										className={clsx(
											styles.entry,
											item.channel === 'email' ? styles.entryEmail : styles.entryInternal,
											!item.isActive && styles.entryInactive
										)}
										style={{ gridRow, gridColumn: entryColumns(item, phasePosition) }}
									>
										<button
											type="button"
											tabIndex={-1}
											aria-hidden="true"
											className={clsx(
												styles.bar,
												item.channel === 'email' ? styles.barEmail : styles.barInternal,
												!item.isActive && styles.barInactive
											)}
											style={{ width: `${barWidthPercent(item, phasePosition, phases.length)}%` }}
											onClick={open}
											title={`${item.title} — ${item.trigger}`}
										/>
										{/*
										 * Auch die Beschriftung ist anklickbar: sie steht oft neben
										 * dem Balken statt darauf, und dorthin zielt man beim Lesen.
										 */}
										<button
											type="button"
											tabIndex={-1}
											aria-hidden="true"
											className={styles.label}
											onClick={open}
											title={`${item.title} — ${item.trigger}`}
										>
											<span className={styles.barIcon}>
												{item.channel === 'email' ? <EmailIcon /> : <InternalIcon />}
											</span>
											{item.isAutomatic && <span className={styles.barAuto}>{t('Automatic')}</span>}
											<span className={styles.barText}>{item.triggerShort}</span>
											{item.pairedWith && (
												<span
													className={styles.barPaired}
													title={
														item.channel === 'email'
															? t('This event also triggers an internal message:')
															: t('This event also triggers an email:')
													}
												>
													⇄
												</span>
											)}
										</button>
									</span>
								</Fragment>
							)
						})}
					</div>
				</div>
			)}
		</div>
	)
}

OverviewGantt.propTypes = {
	data: PropTypes.object,
	onOpen: PropTypes.func.isRequired
}

OverviewGantt.defaultProps = {
	data: null
}
